import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { UiPanelComponent } from '@template-ui/components';
import {
  WorkflowActionTrigger,
  WorkflowDeclaredAction,
  WorkflowDefinitionDocument,
} from './platform-workflow-definition-lifecycle.models';
import { PlatformWorkflowDefinitionLifecycleService } from './platform-workflow-definition-lifecycle.service';
import { sameCapabilityReference } from './workflow-capability-authoring';
import {
  AutomationField,
  AutomationOperation,
  WorkflowAutomationCatalog,
  applyWorkflowAutomation,
  automationEdges,
  automationFingerprint,
  automationOperation,
  automationLiteralError,
  automationShapeError,
  compatibleAutomationContexts,
  nextAutomationId,
  supportsWorkflowAutomation,
} from './workflow-automation-authoring';
import type { WorkflowEditorPendingScope, WorkflowEditorBufferState } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';

let automationEditorIdentity = 0;

@Component({
  selector: 'phms-workflow-automation-editor',
  imports: [CommonModule, FormsModule, UiButtonDirective, UiControlDirective, UiPanelComponent],
  templateUrl: './workflow-automation-editor.component.html',
  styleUrl: './workflow-automation-editor.component.scss',
})
export class WorkflowAutomationEditorComponent implements OnChanges {
  @Input({ required: true }) document!: WorkflowDefinitionDocument;
  @Input() canAuthor = false;
  @Input() interactionBlocked = false;
  @Input() canUndo = false;
  @Output() documentChange = new EventEmitter<WorkflowDefinitionDocument>();
  @Output() pendingChangesChange = new EventEmitter<boolean>();
  @Output() undoRequested = new EventEmitter<void>();
  private readonly service = inject(PlatformWorkflowDefinitionLifecycleService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true }) ?? new WorkflowOwnerViewState();
  readonly catalog = signal<WorkflowAutomationCatalog | null>(null);
  readonly loading = signal(false);
  readonly loadError = signal('');
  readonly operationChooserOpen = signal(false);
  readonly errorId = `workflow-event-error-${++automationEditorIdentity}`;
  triggers: WorkflowActionTrigger[] = [];
  triggerIndex = 0;
  actionIndex = 0;
  operationChoice = '';
  error = '';
  private pending = false;
  private original = '';
  private originalContext = '';
  private originalTriggers: WorkflowActionTrigger[] = [];
  private catalogKey = '';
  private request = 0;
  private focusRequest = 0;
  private readonly literalDrafts = new Map<string, string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.document) return;
    if (changes['document'] && !this.pending) this.reload();
    const key = JSON.stringify([
      this.document.workflowCode,
      this.document.packReference,
    ]);
    if (key !== this.catalogKey) {
      this.catalogKey = key;
      this.load();
    }
  }
  private reload(): void {
    const triggerId = this.selectedTrigger?.id, actionId = this.selectedAction?.id;
    const sameContext = this.documentContext(this.document) === this.originalContext;
    this.triggers = automationShapeError(this.document.triggers) ? [] : structuredClone(this.document.triggers ?? []);
    this.originalTriggers = structuredClone(this.triggers);
    this.original = automationFingerprint(this.document);
    this.originalContext = this.documentContext(this.document);
    const triggers = this.triggers.flatMap((trigger, index) => trigger.id === triggerId ? [index] : []);
    this.triggerIndex = sameContext && triggers.length === 1 ? triggers[0] : 0;
    const actions = this.selectedTrigger?.actions.flatMap((action, index) => action.id === actionId ? [index] : []) ?? [];
    this.actionIndex = sameContext && actions.length === 1 ? actions[0] : 0;
    this.operationChooserOpen.set(false);
    this.focusRequest++;
    this.literalDrafts.clear();
    this.error = '';
  }
  load(): void {
    if (this.destroyRef.destroyed) return;
    const request = ++this.request,
      key = this.catalogKey;
    this.loading.set(true);
    this.loadError.set('');
    this.catalog.set(null);
    this.service
      .getAutomationAuthoringCatalog(this.document)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (catalog) => {
          if (request !== this.request || key !== this.catalogKey) return;
          this.loading.set(false);
          if (catalog.contractVersion !== 1) {
            this.loadError.set(
              'This automation catalog version is not supported. Saved declarations are retained.',
            );
            return;
          }
          this.catalog.set(catalog);
          this.operationChoice = this.availableOperations()[0]
            ? this.operationId(this.availableOperations()[0])
            : '';
        },
        error: () => {
          if (request !== this.request || key !== this.catalogKey) return;
          this.loading.set(false);
          this.loadError.set(
            'Automated operations could not be loaded for this workflow and pack. Your declarations and entered values are kept. Retry availability before applying changes.',
          );
        },
      });
  }
  get selectedTrigger(): WorkflowActionTrigger | null {
    return this.triggers[this.triggerIndex] ?? null;
  }
  get selectedAction(): WorkflowDeclaredAction | null {
    return this.selectedTrigger?.actions[this.actionIndex] ?? null;
  }
  get selectedOperation(): AutomationOperation | null {
    return automationOperation(this.catalog(), this.selectedAction);
  }
  get chosenOperation(): AutomationOperation | null {
    const matches = this.catalog()?.operations.filter(operation => this.operationId(operation) === this.operationChoice) ?? [];
    return matches.length === 1 ? matches[0] : null;
  }
  get ambiguousOperationChoice(): boolean {
    return (this.catalog()?.operations.filter(operation => this.operationId(operation) === this.operationChoice).length ?? 0) > 1;
  }
  get ambiguousCatalogOperations(): boolean {
    return this.catalog()?.operations.some(operation => this.operationReferenceAmbiguous(operation)) ?? false;
  }
  private operationReferenceAmbiguous(operation: AutomationOperation): boolean {
    return (this.catalog()?.operations.filter(candidate => sameCapabilityReference(candidate.command, operation.command)).length ?? 0) > 1;
  }
  operationCanBeAdded(operation: AutomationOperation): boolean { return operation.availableForNewUse && !this.operationReferenceAmbiguous(operation); }
  get retainedLiteralDrafts(): { field: string; text: string }[] {
    const prefix = this.selectedAction?.id + '/';
    return [...this.literalDrafts.entries()].filter(([key]) => key.startsWith(prefix))
      .map(([key, text]) => ({ field: key.slice(prefix.length), text }));
  }
  get readOnlyReason(): string {
    if (!supportsWorkflowAutomation(this.document.payloadSchemaVersion))
      return 'This saved workflow format is unsupported. Its original event declarations are retained read-only.';
    return automationShapeError(this.document.triggers) ?? '';
  }
  get staleSource(): boolean {
    return this.pending && (this.documentContext(this.document) !== this.originalContext || automationFingerprint(this.document) !== this.original);
  }
  get disabled(): boolean {
    return (
      !this.canAuthor ||
      this.destroyRef.destroyed ||
      !!this.readOnlyReason ||
      this.interactionBlocked ||
      this.loading() ||
      !this.catalog()
    );
  }
  get actionDisabled(): boolean {
    return this.disabled || !this.selectedOperation?.availableForNewUse;
  }
  get humanNodes() {
    return this.document.nodes.filter((node) => node.typeCode === 'human_step');
  }
  get selectedNodeAvailable(): boolean {
    return this.humanNodes.some(
      (node) => node.id === this.selectedTrigger?.nodeId,
    );
  }
  get pathChoices() {
    const trigger = this.selectedTrigger;
    return trigger
      ? automationEdges(this.document, trigger.nodeId).filter(
          (edge) => trigger.kind !== 'return' || edge.pathKindCode === 'return',
        )
      : [];
  }
  get selectedPathAvailable(): boolean { return this.pathChoices.some(edge => edge.id === this.selectedTrigger?.edgeId); }
  get selectedOutcomeAvailable(): boolean { return this.outcomeChoices.includes(this.selectedTrigger?.outcomeCode ?? ''); }
  get outcomeChoices(): string[] {
    const trigger = this.selectedTrigger;
    return trigger
      ? [
          ...new Set(
            automationEdges(this.document, trigger.nodeId)
              .filter(
                (edge) =>
                  edge.fromNodeId !== trigger.nodeId && edge.outcomeCode,
              )
              .map((edge) => edge.outcomeCode!),
          ),
        ]
      : [];
  }
  availableOperations(): AutomationOperation[] {
    return (
      this.catalog()?.operations.filter(
        (operation) => this.operationCanBeAdded(operation),
      ) ?? []
    );
  }
  operationId(operation: AutomationOperation): string {
    return JSON.stringify([operation.command.key.provider, operation.command.key.kind, operation.command.key.code,
      operation.command.key.version, operation.command.inputSchemaHash, operation.command.outputSchemaHash,
      operation.command.contractHash, operation.moduleCode, operation.moduleVersion, operation.moduleHash]);
  }
  actionLabel(action: WorkflowDeclaredAction): string {
    return (
      automationOperation(this.catalog(), action)?.label ??
      action.command?.key?.code ??
      'Unavailable saved operation'
    );
  }
  nodeLabel(id: string): string {
    const node = this.document.nodes.find((item) => item.id === id);
    return node?.displayName || node?.stepCode || id;
  }
  eventLabel(kind: string): string {
    return (
      (
        {
          entry: 'When work arrives',
          exit: 'When work leaves',
          transition: 'Along a selected path',
          return: 'Along a return path',
          decision: 'When a decision is recorded',
          'assignment-change': 'When assignment changes',
        } as Record<string, string>
      )[kind] ?? `Saved event · ${kind}`
    );
  }
  selectTrigger(index: number): void {
    if (this.interactionBlocked || this.destroyRef.destroyed || !this.triggers[index]) return;
    this.focusRequest++;
    this.operationChooserOpen.set(false);
    this.triggerIndex = index;
    this.actionIndex = 0;
  }
  selectAction(index: number): void {
    if (this.interactionBlocked || this.destroyRef.destroyed || !this.selectedTrigger?.actions[index]) return;
    this.focusRequest++;
    this.operationChooserOpen.set(false);
    this.actionIndex = index;
  }
  openOperationChooser(): void {
    if (this.disabled || !this.selectedTrigger) return;
    this.operationChooserOpen.set(!this.operationChooserOpen());
    if (this.operationChooserOpen()) this.scheduleFocus('[data-operation-chooser] select');
  }
  private changed(): void {
    this.pending = true;
    this.error = '';
    this.pendingChangesChange.emit(true);
  }
  addTrigger(): void {
    if (this.disabled || !this.humanNodes.length || this.triggers.length >= 64)
      return;
    this.triggers.push({
      id: nextAutomationId(
        'trigger',
        this.triggers.map((item) => item.id),
      ),
      kind: 'entry',
      nodeId: this.humanNodes[0].id,
      actions: [],
    });
    this.triggerIndex = this.triggers.length - 1;
    this.actionIndex = 0;
    this.operationChooserOpen.set(false);
    this.changed();
  }
  removeTrigger(): void {
    if (this.disabled || !this.selectedTrigger) return;
    this.selectedTrigger.actions.forEach(action => this.forgetLiteralDrafts(action));
    this.triggers.splice(this.triggerIndex, 1);
    this.triggerIndex = Math.max(0, this.triggerIndex - 1);
    this.actionIndex = 0;
    this.changed();
  }
  updateTrigger(patch: Partial<WorkflowActionTrigger>): void {
    if (this.disabled || !this.selectedTrigger) return;
    this.triggers[this.triggerIndex] = { ...this.selectedTrigger, ...patch };
    this.changed();
  }
  setEvent(kind: string): void {
    this.updateTrigger({ kind, edgeId: null, outcomeCode: null });
  }
  setNode(nodeId: string): void {
    this.updateTrigger({ nodeId, edgeId: null, outcomeCode: null });
  }
  addAction(): void {
    const operation = this.chosenOperation,
      trigger = this.selectedTrigger;
    if (this.disabled || !operation || !this.operationCanBeAdded(operation) || !trigger || trigger.actions.length >= 32)
      return;
    const id = nextAutomationId(
      'action',
      this.triggers.flatMap((item) => item.actions.map((action) => action.id)),
    );
    const existingGroup = trigger.actions.find(
      (item) => item.group === 'default',
    );
    const group =
      existingGroup && !operation.modes.includes(existingGroup.mode)
        ? id
        : 'default';
    const orders = trigger.actions
      .filter((item) => item.group === group)
      .map((item) => item.order);
    const order = Array.from({ length: 32 }, (_, index) => index).find(
      (index) => !orders.includes(index),
    )!;
    const action: WorkflowDeclaredAction = {
      id,
      typeCode: 'command',
      command: structuredClone(operation.command),
      inputs: [],
      outcomes: [...operation.outcomes],
      group,
      order,
      mode:
        group === 'default' && existingGroup
          ? existingGroup.mode
          : operation.modes.includes('durable')
            ? 'durable'
            : operation.modes[0],
      failurePolicy:
        group === 'default' && existingGroup
          ? existingGroup.failurePolicy
          : 'stop',
      caller: 'scoped-system',
      requiredForProgression: false,
    };
    for (const field of operation.input.fields.filter(
      (item) => item.required,
    )) {
      const context = compatibleAutomationContexts(
        this.catalog()!,
        trigger.kind,
        field,
        this.document,
      ).find(
        (item) =>
          item.code ===
          (
            {
              occurrenceId: 'occurrence.id',
              expectedETag: 'workflow.etag',
            } as Record<string, string>
          )[field.name],
      );
      action.inputs.push(
        context
          ? { field: field.name, source: 'context', context: context.code }
          : {
              field: field.name,
              source: 'literal',
              value: this.defaultValue(field),
            },
      );
    }
    trigger.actions.push(action);
    this.actionIndex = trigger.actions.length - 1;
    this.operationChooserOpen.set(false);
    this.changed();
  }
  removeAction(): void {
    if (this.disabled || !this.selectedTrigger || !this.selectedAction) return;
    this.forgetLiteralDrafts(this.selectedAction);
    this.selectedTrigger.actions.splice(this.actionIndex, 1);
    this.actionIndex = Math.max(0, this.actionIndex - 1);
    this.changed();
  }
  private forgetLiteralDrafts(action: WorkflowDeclaredAction): void {
    for (const key of this.literalDrafts.keys()) if (key.startsWith(action.id + '/')) this.literalDrafts.delete(key);
  }
  updateAction(patch: Partial<WorkflowDeclaredAction>): void {
    if (this.actionDisabled || !this.selectedTrigger || !this.selectedAction)
      return;
    this.selectedTrigger.actions[this.actionIndex] = {
      ...this.selectedAction,
      ...patch,
    };
    this.changed();
  }
  binding(field: AutomationField) {
    return this.selectedAction?.inputs.find(
      (input) => input.field === field.name,
    );
  }
  contextChoices(field: AutomationField) {
    return this.catalog() && this.selectedTrigger
      ? compatibleAutomationContexts(
          this.catalog()!,
          this.selectedTrigger.kind,
          field,
          this.document,
        )
      : [];
  }
  bindingContextUnavailable(field: AutomationField): boolean {
    const binding = this.binding(field);
    return (
      binding?.source === 'context' &&
      !this.contextChoices(field).some(
        (context) => context.code === binding.context,
      )
    );
  }
  setSource(field: AutomationField, source: string): void {
    if (this.actionDisabled || !this.selectedAction) return;
    const inputs = this.selectedAction.inputs.filter(
      (input) => input.field !== field.name,
    );
    if (source === 'literal')
      inputs.push({
        field: field.name,
        source,
        value: this.defaultValue(field),
      });
    else if (source === 'context')
      inputs.push({
        field: field.name,
        source,
        context: this.contextChoices(field)[0]?.code ?? '',
      });
    this.literalDrafts.delete(this.literalKey(field));
    this.updateAction({ inputs });
  }
  setContext(field: AutomationField, context: string): void {
    this.replaceBinding(field, {
      field: field.name,
      source: 'context',
      context,
    });
  }
  private replaceBinding(
    field: AutomationField,
    binding: WorkflowDeclaredAction['inputs'][number],
  ): void {
    if (!this.selectedAction) return;
    this.updateAction({
      inputs: [
        ...this.selectedAction.inputs.filter(
          (input) => input.field !== field.name,
        ),
        binding,
      ],
    });
  }
  private defaultValue(field: AutomationField): unknown {
    return field.type === 'boolean'
      ? false
      : field.type === 'array'
        ? []
        : field.type === 'string'
          ? (field.values?.[0] ?? '')
          : null;
  }
  literalKey(field: AutomationField): string {
    return `${this.selectedAction?.id}/${field.name}`;
  }
  literalText(field: AutomationField): string {
    const value = this.binding(field)?.value;
    return (
      this.literalDrafts.get(this.literalKey(field)) ??
      (field.type === 'array'
        ? JSON.stringify(value ?? [], null, 2)
        : value == null
          ? ''
          : String(value))
    );
  }
  setLiteral(field: AutomationField, text: string): void {
    if (this.actionDisabled) return;
    text = String(text);
    this.literalDrafts.set(this.literalKey(field), text);
    let value: unknown = text;
    if (field.type === 'integer' || field.type === 'number')
      value = text.trim() ? Number(text) : null;
    else if (field.type === 'boolean')
      value = text === 'true' ? true : text === 'false' ? false : null;
    else if (field.type === 'array') {
      try {
        value = JSON.parse(text);
      } catch {
        value = text;
      }
    }
    this.replaceBinding(field, { field: field.name, source: 'literal', value });
  }
  setNull(field: AutomationField): void {
    if (this.actionDisabled || !field.nullable) return;
    this.literalDrafts.delete(this.literalKey(field));
    this.replaceBinding(field, {
      field: field.name,
      source: 'literal',
      value: null,
    });
  }
  fieldLabel(field: AutomationField): string {
    if (field.name === 'eTag') return 'ETag';
    if (this.selectedOperation?.command.key.provider === 'workflow' && this.selectedOperation.command.key.code === 'workflow.review.request' && this.selectedOperation.command.key.version === 1) {
      const label = ({ occurrenceId: 'Step visit', expectedETag: 'Expected revision', workSummary: 'Work summary' } as Record<string, string>)[field.name];
      if (label) return label;
    }
    return field.name
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (letter) => letter.toUpperCase());
  }
  contextLabel(code: string, label: string): string {
    return code === 'occurrence.id' ? 'Step visit ID' : code === 'workflow.etag' ? 'Current workflow ETag' : label;
  }
  fieldIssue(field: AutomationField): string | null {
    return this.selectedAction && this.selectedTrigger ? this.inputIssue(field, this.selectedAction, this.selectedTrigger) : null;
  }
  private inputIssue(field: AutomationField, action: WorkflowDeclaredAction, trigger: WorkflowActionTrigger): string | null {
    const binding = action.inputs.find(input => input.field === field.name);
    if (!binding) return field.required ? 'Choose a value source for this required input.' : null;
    if (binding.source === 'literal') return automationLiteralError(field, binding.value);
    return binding.source !== 'context' || !this.catalog() || !compatibleAutomationContexts(this.catalog()!, trigger.kind, field, this.document)
      .some(context => context.code === binding.context) ? 'Choose a compatible event value.' : null;
  }
  hasPendingChanges(): boolean {
    return this.pending;
  }
  private documentContext(document: WorkflowDefinitionDocument): string {
    return JSON.stringify([document.workflowCode, document.definitionVersionCode, document.packReference]);
  }
  /** Trigger edits remain one atomic declaration set, retaining unsupported saved entries. */
  pendingEditorScopes(latestDocument: () => WorkflowDefinitionDocument | null = () => this.document): readonly WorkflowEditorPendingScope[] {
    return [{
      id: 'automation-triggers', label: 'automated trigger input',
      state: (): WorkflowEditorBufferState => {
        if (!this.pending) return 'clean';
        const document = latestDocument();
        if (!document || this.documentContext(document) !== this.originalContext || automationFingerprint(document) !== this.original) return 'stale';
        if (this.disabled) return 'invalid';
        try { applyWorkflowAutomation(document, this.triggers, this.catalog()!, this.originalTriggers); return 'valid'; } catch { return 'invalid'; }
      },
      fingerprint: () => {
        const document = latestDocument();
        return JSON.stringify([this.pending, this.original, this.originalContext, this.originalTriggers, this.triggers,
          [...this.literalDrafts.entries()].sort(([left], [right]) => left.localeCompare(right)),
          this.canAuthor, this.interactionBlocked, this.loading(), this.catalog(),
          document && [this.documentContext(document), automationFingerprint(document), document.payloadSchemaVersion, document.subjectTypeCode]]);
      },
      apply: () => { const document = latestDocument(); return !!document && this.applyPendingChanges(document); },
      discard: () => { const document = latestDocument(); if (document) this.document = document; this.discardPendingChanges(); },
      focus: () => this.focusPendingChanges(),
    }];
  }
  applyPendingChanges(document = this.document): boolean {
    if (!this.pending) return true;
    if (this.disabled) return false;
    if (this.documentContext(document) !== this.originalContext || automationFingerprint(document) !== this.original) {
      this.error =
        'The workflow, paths or saved automation changed while you were editing. Discard these edits and reopen the current declaration.';
      this.focusPendingChanges();
      return false;
    }
    try {
      const next = applyWorkflowAutomation(
        document,
        this.triggers,
        this.catalog()!,
        this.originalTriggers,
      );
      this.document = next;
      this.pending = false;
      this.pendingChangesChange.emit(false);
      this.documentChange.emit(next);
      this.original = automationFingerprint(next);
      this.originalTriggers = structuredClone(this.triggers);
      this.error = '';
      return true;
    } catch (error) {
      this.error =
        error instanceof Error
          ? error.message
          : 'Review the automated declarations.';
      this.focusPendingChanges();
      return false;
    }
  }
  discardPendingChanges(): void {
    this.pending = false;
    this.reload();
    this.pendingChangesChange.emit(false);
  }
  focusPendingChanges(): void {
    if (this.error) {
      for (let triggerIndex = 0; triggerIndex < this.triggers.length; triggerIndex++) {
        const trigger = this.triggers[triggerIndex];
        const actionIndex = trigger.actions.findIndex(action => automationOperation(this.catalog(), action)?.input.fields.some(field => this.inputIssue(field, action, trigger)));
        if (actionIndex >= 0) { this.selectTrigger(triggerIndex); this.selectAction(actionIndex); break; }
      }
    }
    this.scheduleFocus('[data-automation-error]');
  }
  private scheduleFocus(selector: string, inputField?: string): void {
    const request = ++this.focusRequest, context = this.documentContext(this.document);
    setTimeout(() => {
      if (request !== this.focusRequest || this.destroyRef.destroyed || context !== this.documentContext(this.document) ||
        this.ownerViews.focusSuspended() || this.element.nativeElement.closest('[hidden],[inert]')) return;
      const field = inputField ? Array.from(this.element.nativeElement.querySelectorAll<HTMLElement>('[data-input-field]'))
        .find(element => element.dataset['inputField'] === inputField) : null;
      (field?.querySelector<HTMLElement>('.automation__input-value input, .automation__input-value textarea, .automation__input-value select') ??
        this.element.nativeElement.querySelector<HTMLElement>(selector) ??
        this.element.nativeElement.querySelector<HTMLElement>('[data-event-heading]'))?.focus();
    });
  }
  focusPath(path: string): void {
    const triggerId = /^\$\.triggers\[id=([^\]]+)\]/.exec(path)?.[1],
      actionId = /\.actions\[id=([^\]]+)\]/.exec(path)?.[1];
    const trigger = this.triggers.findIndex(
      (item) =>
        item.id === triggerId ||
        item.actions.some((action) => action.id === actionId),
    );
    if (trigger >= 0) {
      this.selectTrigger(trigger);
      const action = this.selectedTrigger!.actions.findIndex(
        (item) => item.id === actionId,
      );
      if (action >= 0) this.selectAction(action);
    }
    const field = /\.inputs\[field=([^\]]+)\]/.exec(path)?.[1];
    if (field) this.scheduleFocus('[data-automation-error]', field);
    else this.focusPendingChanges();
  }
}
